import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusOnFirstErrorField } from '../../../hooks/errorFieldFocus';
import { useValidator } from '../../../hooks/formValidator/useValidator';
import Button from '../../../ui/Button';
import useFormRefs from './useFormRefs';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../../app.config.vars/routes';
import FormFields from './formFields';
import Headline from '../../../ui/Headline';
import useTaskFormState from './useFormState';
import useTaskFormValidationSchema, { buildInputValidationSchema } from './useValidationSchema';
import { generateIdRoutePath, generateTaskRoutePath } from '../../../helpers';
import { saveAdminTask, sortTaskAnswers, updateAdminTask } from '../../../api/admin/tasks';
import { handleFileUploadChange } from '../helpers';
import { generateSaveTaskPayload } from '../payloads/saveTaskPayload';
import { generateUpdateTaskPayload } from '../payloads/updateTaskPayload';
import { validateAllFields, validateAnswersBeforeSubmit, validateAsDraft, validateInputTaskAnswersBeforeSubmit, validateUpdatedFormFieldsAsDraft, } from './validateFormFields';
const AdminTaskForm = ({ taskTypes, formType, rallyId, taskId }) => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const [saveAsDraft, setSaveAsDraft] = useState(false);
    const [taskUpdated, setTaskUpdated] = useState(true);
    const { formState, updatedFormFields, setUpdatedFormFields, isFormStateReady } = useTaskFormState({ taskId, updated: taskUpdated, setUpdated: setTaskUpdated });
    const { inputsValidationSchema, selectsValidationSchema } = useTaskFormValidationSchema({
        taskType: formState.selectedTaskType,
        saveAsDraft,
    });
    const { formErrors, validateAll, validateSelect, validateInputField } = useValidator({
        inputSchema: inputsValidationSchema,
        selectSchema: selectsValidationSchema,
    });
    const { refs, orderedRefs } = useFormRefs({ taskType: formState.selectedTaskType, formState });
    const [taskAnswersError, setTaskAnswersError] = useState(null);
    const [inputTaskAnswersError] = useState(null);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [forceFocus, setForceFocus] = useState(false);
    const [sortedTaskAnswers, setSortedTaskAnswers] = useState([]);
    useFocusOnFirstErrorField(formErrors, orderedRefs, forceFocus, setForceFocus);
    useEffect(() => {
        if (formState.taskStatus === 1) {
            setSaveAsDraft(false);
        }
    }, [formState.taskStatus]);
    useEffect(() => {
        if (isFormStateReady && formType === 'edit') {
            const customInputSchema = buildInputValidationSchema(formState.selectedTaskType, false, 'edit', !!formState.descriptionTranslations[i18n.language] ||
                !!formState.descriptionTranslations.en ||
                !!formState.descriptionTranslations.de ||
                !!formState.descriptionTranslations.cs);
            validateAllFields({ validateAll, formState, customInputSchema });
            if (formState.selectedTaskType === 'single-selection' ||
                formState.selectedTaskType === 'multiple-selection') {
                validateAnswersBeforeSubmit({
                    taskAnswers: formState.taskAnswers,
                    selectedTaskType: formState.selectedTaskType,
                    setTaskAnswersError,
                    validateAll,
                });
            }
            else if (formState.selectedTaskType === 'validated-text') {
                validateInputTaskAnswersBeforeSubmit({
                    inputTaskAnswers: formState.inputTaskAnswers,
                    validateAll,
                });
            }
        }
    }, [isFormStateReady]);
    const handleMediaUploadChange = (file) => {
        handleFileUploadChange({
            file,
            setFile: formState.setFileReference,
            fieldName: 'fileReference',
            setUpdatedFormFields,
        });
    };
    const returnToTasksList = () => {
        navigate(generateIdRoutePath(APP_ROUTES.adminManageTasks, rallyId));
    };
    const handleSaveTask = async ({ action, saveAsDraft, }) => {
        setSaveAsDraft(saveAsDraft);
        let errors;
        const customInputSchema = buildInputValidationSchema(formState.selectedTaskType, saveAsDraft, 'create', !!formState.descriptionTranslations[i18n.language] ||
            !!formState.descriptionTranslations.en ||
            !!formState.descriptionTranslations.de ||
            !!formState.descriptionTranslations.cs);
        if (saveAsDraft) {
            errors = validateAsDraft({ validateAll, formState, customInputSchema });
        }
        else {
            errors = validateAllFields({
                validateAll,
                formState,
                customInputSchema,
            });
        }
        let answerErrors = {};
        if (!saveAsDraft &&
            (formState.selectedTaskType === 'single-selection' ||
                formState.selectedTaskType === 'multiple-selection')) {
            answerErrors = validateAnswersBeforeSubmit({
                taskAnswers: formState.taskAnswers,
                selectedTaskType: formState.selectedTaskType,
                setTaskAnswersError,
                validateAll,
            });
        }
        if (!saveAsDraft && formState.selectedTaskType === 'validated-text') {
            answerErrors = validateInputTaskAnswersBeforeSubmit({
                inputTaskAnswers: formState.inputTaskAnswers,
                validateAll,
            });
        }
        if (Object.keys(errors).length > 0) {
            setForceFocus(true);
            return;
        }
        if (Object.keys(answerErrors).length > 0) {
            return;
        }
        if (!saveAsDraft) {
            if (taskAnswersError || inputTaskAnswersError) {
                return;
            }
        }
        setSubmitStatus('pending');
        try {
            const res = await saveAdminTask(rallyId, generateSaveTaskPayload({
                formState,
                saveAsDraft,
            }));
            const taskId = res.data.uid;
            if (action === 'return') {
                returnToTasksList();
            }
            else {
                navigate(generateTaskRoutePath(APP_ROUTES.adminEditTask, rallyId, taskId));
            }
        }
        catch (error) {
            setSubmitStatus('error');
            // TODO handle error
        }
    };
    const handleEditTask = async ({ action }) => {
        if (updatedFormFields.size) {
            setSaveAsDraft(formState.taskStatus === 0);
            if (formState.taskStatus === 0) {
                const customInputSchema = buildInputValidationSchema(formState.selectedTaskType, true, 'edit');
                const errors = validateUpdatedFormFieldsAsDraft({
                    validateInputField,
                    updatedFormFields,
                    formState,
                    refs,
                    customInputSchema,
                });
                if (errors.length) {
                    errors[0].current.focus();
                    return;
                }
            }
            else {
                const customInputSchema = buildInputValidationSchema(formState.selectedTaskType, false, 'edit', !!formState.descriptionTranslations[i18n.language] ||
                    !!formState.descriptionTranslations.en ||
                    !!formState.descriptionTranslations.de ||
                    !!formState.descriptionTranslations.cs);
                const errors = validateAllFields({ validateAll, formState, customInputSchema });
                let answerErrors = {};
                if (formState.selectedTaskType === 'single-selection' ||
                    formState.selectedTaskType === 'multiple-selection') {
                    answerErrors = validateAnswersBeforeSubmit({
                        taskAnswers: formState.taskAnswers,
                        selectedTaskType: formState.selectedTaskType,
                        setTaskAnswersError,
                        validateAll,
                    });
                }
                if (formState.selectedTaskType === 'validated-text') {
                    answerErrors = validateInputTaskAnswersBeforeSubmit({
                        inputTaskAnswers: formState.inputTaskAnswers,
                        validateAll,
                    });
                }
                if (Object.keys(errors).length > 0) {
                    setForceFocus(true);
                    return;
                }
                if (Object.keys(answerErrors).length > 0) {
                    return;
                }
                if (taskAnswersError || inputTaskAnswersError) {
                    return;
                }
            }
            setSubmitStatus('pending');
            try {
                await updateAdminTask(taskId, generateUpdateTaskPayload({
                    formState,
                    updatedFormFields,
                }));
                setSubmitStatus('success');
                setUpdatedFormFields(new Set());
                if (action === 'return') {
                    returnToTasksList();
                }
                else {
                    setTaskUpdated(true);
                }
            }
            catch (error) {
                setSubmitStatus('error');
                // TODO handle error
            }
        }
        else {
            if (sortedTaskAnswers.length) {
                setSubmitStatus('pending');
                try {
                    await sortTaskAnswers(taskId, sortedTaskAnswers);
                    setSubmitStatus('success');
                    if (action === 'return') {
                        returnToTasksList();
                    }
                    else {
                        setTaskUpdated(true);
                        setSortedTaskAnswers([]);
                    }
                }
                catch (error) {
                    setSubmitStatus('error');
                    // TODO handle error
                }
            }
        }
    };
    const handleTaskSubmit = async ({ formType, action, }) => {
        if (formType === 'create') {
            await handleSaveTask({
                action,
                saveAsDraft: false,
            });
        }
        else {
            await handleEditTask({ action });
        }
    };
    return (_jsxs("form", { className: 'task-form', onSubmit: (event) => event.preventDefault(), children: [_jsx("div", { className: "page-header-top", children: _jsx(Headline, { title: formType === 'create'
                        ? t('admin.create_task')
                        : formState.titleTranslations[i18n.language], type: 'h1' }) }), _jsx(FormFields, { taskTypes: taskTypes, handleMediaUploadChange: handleMediaUploadChange, refs: refs, formErrors: formErrors, taskAnswersError: taskAnswersError, setTaskAnswersError: setTaskAnswersError, formState: formState, validateSelect: validateSelect, validateInputField: validateInputField, updatedFormFields: updatedFormFields, setUpdatedFormFields: setUpdatedFormFields, formType: formType, setSortedTaskAnswers: setSortedTaskAnswers, taskId: taskId, setTaskUpdated: setTaskUpdated, isFormStateReady: isFormStateReady, validateAll: validateAll }), submitStatus === 'error' && _jsx("div", { className: "form-submit-error-message", children: t('') }), _jsx("div", { className: "task-form-submit", children: _jsxs(ButtonsGroup, { children: [_jsx(Button, { type: "submit", onClick: () => handleTaskSubmit({ formType, action: 'stay' }), label: t(formType === 'create' ? 'button.save' : 'button.save_changes'), disabled: submitStatus === 'pending' }), _jsx(Button, { type: "submit", variant: 'outlined', onClick: () => handleTaskSubmit({ formType, action: 'return' }), label: t('button.save_and_return_to_list'), disabled: submitStatus === 'pending' }), formType === 'create' && (_jsx(Button, { type: "submit", variant: 'outlined', onClick: () => handleSaveTask({ action: 'return', saveAsDraft: true }), label: t('button.save_draft') })), _jsx(Button, { variant: 'outlined', onClick: returnToTasksList, label: t('button.cancel') })] }) })] }));
};
export default AdminTaskForm;
